<template>
    <div class="personal-sidebar" :class="{ 'mobile-nav': isMobile, open: isMobile && mobileStore.isMobileNavOpen }">
        <!-- Mobile Header -->
        <div v-if="isMobile" class="mobile-header">
            <sidebar-logo class="mobile-logo" @click="navigateToDashboard" />
            <button class="close-button" @click="mobileStore.closeMobileNav">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
            </button>
        </div>

        <div class="px-4 mb-3 flex lg:hidden">
            <HeaderAccount v-if="store.loggedIn" class="flex w-full" />
        </div>

        <!-- Desktop Logo -->
        <div class="h-24 lg:flex hidden items-center justify-center">
            <sidebar-logo class="mx-auto cursor-pointer" @click="navigateToDashboard" />
        </div>
        <map-illustration class="world-illustration" />

        <!-- sub banner -->
        <sub-status v-if="store.loggedIn" />

        <!-- nav items -->
        <div class="nav-content">
            <div class="top-nav-container">
                <nuxt-link class="nav-item" :class="{ 'current-route': route.path === '/dashboard' }" href="/dashboard">
                <PhGauge :size="20" class="icon-color" />
                <span>Dashboard</span>
            </nuxt-link>

            <nuxt-link class="nav-item" :class="{ 'current-route': route.path === '/vault' }" href="/vault">
                <PhVault :size="20" class="icon-color" />
                <span>Identity Vault</span>
            </nuxt-link>

            <nuxt-link class="nav-item" :class="{ 'current-route': route.path === '/data-brokers' }" href="/data-brokers">
                <PhListMagnifyingGlass :size="20" class="icon-color" />

                <span>Data Brokers</span>
            </nuxt-link>

            <nuxt-link @click="shouldShowSearchPresenceNewIndicator = false" class="nav-item" :class="{ 'current-route': route.path === '/search-presence' }" href="/search-presence">
                <PhEye :size="20" class="icon-color" />
                <div class="w-full flex justify-between">
                    <span>Search Presence</span>

                    <StatusIndicator v-if="shouldShowSearchPresenceNewIndicator" class="!flex" variant="warning" />
                </div>
            </nuxt-link>

            <nuxt-link class="nav-item" :class="{ 'current-route': route.path === '/dark-web-scan' }" href="/dark-web-scan">
                <PhShieldWarning :size="20" class="icon-color" />
                <span>Dark Web Presence</span>
            </nuxt-link>

            <nuxt-link class="nav-item" :class="{ 'current-route': route.path === '/family' }" href="/family" v-if="!storePersonal?.impersonating || store?.user?.identity?.is_pb">
                <PhAddressBook :size="20" class="icon-color" />

                <span>Family Protection</span>
            </nuxt-link>

            <nuxt-link class="nav-item" :class="{ 'current-route': route.path === '/reports' }" href="/reports" v-if="!storePersonal?.impersonating || (storePersonal?.impersonating && store?.user?.identity?.is_pb)">
                <PhFiles :size="20" class="icon-color" />

                <span>Reports</span>
            </nuxt-link>

            <nuxt-link class="nav-item" :class="{ 'current-route': route.path.startsWith('/settings') }"  href="/settings">
                <PhGearSix :size="20" class="icon-color" />
                <div class="w-full flex justify-between">
                    <span>Account Settings</span>

                    <StatusIndicator v-if="shouldShowSignLpoaIndicator" class="!flex" variant="warning" />
                </div>
            </nuxt-link>
            </div>
        </div>

        <div class="bottom-section">
            <work-switch />
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import SidebarLogo from '~/assets/images/branding/sidebar-logo.svg';
import MapIllustration from '~/assets/images/illustrations/map.svg';
import { useAuthStore } from '~/stores/auth';
import { useMobileStore } from '~/stores/layout/mobile';

import { PhAddressBook, PhEye, PhFiles, PhGauge, PhGearSix, PhListMagnifyingGlass, PhShieldWarning, PhVault } from "@phosphor-icons/vue";
import StatusIndicator from "~/components/main/shared/StatusIndicator.vue";
import HeaderAccount from '../Header/HeaderAccount.vue';
import SubStatus from './SubscriptionStatusBlock.vue';
import WorkSwitch from './WorkSwitch.vue';

const store = useAuthStore();
const mobileStore = useMobileStore();
const route = useRoute();
const router = useRouter();
const storePersonal = usePersonStore();

const { isMobile } = useIsMobile();
const { lockScroll, unlockScroll } = useScrollLock();

/**
 * Navigate to dashboard while preserving impersonation
 */
const navigateToDashboard = () => {
    router.push({ path: '/dashboard', query: route.query });
};

onUnmounted(() => {
    unlockScroll();
});

// Watch for mobile nav open/close to lock/unlock scroll
watch(
    () => mobileStore.isMobileNavOpen,
    (open) => {
        if (open && isMobile.value) lockScroll();
        else unlockScroll();
    }
);

// Close mobile nav when route changes
watch(
    () => route.path,
    () => {
        if (isMobile.value) {
            mobileStore.closeMobileNav();
        }
    }
);

const shouldShowSearchPresenceNewIndicator = ref(window.localStorage.getItem('pb-should-hide-search-presence-new-indicator') !== '1');

const shouldShowSignLpoaIndicator = computed(() => !storePersonal.person?.poa_signed)

const canSeeDarkWebScan = computed(() => {
    return store?.user?.identity?.is_pb || store?.user?.employer?.id === 19381;
});
</script>

<style lang="scss" scoped>
.world-illustration {
    @apply fixed scale-[3.5] -z-10 pointer-events-none hidden lg:block;
}

.personal-sidebar {
    @apply fixed z-100 w-[280px] h-full overflow-hidden bg-zinc-950 transition-transform duration-300 ease-in-out flex flex-col;

    @media (max-width: 1023.98px) {
        width: 100%;
        transform: translateX(-100%);
    }

    &.mobile-nav {
        transform: translateX(-100%);

        &.open {
            transform: translateX(0);
        }
    }

    .mobile-header {
        @apply flex justify-between items-center p-4 shrink-0;

        .mobile-logo {
            @apply w-44 cursor-pointer;
        }

        .close-button {
            @apply bg-none border-none text-zinc-50 cursor-pointer p-2 flex items-center justify-center;
        }
    }
}

.nav-content {
    @apply flex-1 overflow-y-auto min-h-0;
}

.top-nav-container {
    @apply px-4 py-4;
}

.bottom-section {
    @apply p-4 bg-black shrink-0 w-full space-y-4 mt-auto;
}

.nav-item {
    @apply flex justify-start items-center gap-3 px-4 py-2 cursor-pointer rounded-md text-white;
    background: linear-gradient(to right, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0));
    transition: all ease-in-out 0.2s;
    user-select: none;

    .icon-color{
        @apply text-zinc-500;
    }

    &:hover ,  &.current-route {
        @apply bg-zinc-900 rounded text-white ;
        --svg-fill: #fff;

        .icon {
            @apply text-white;
        }

        svg {
            @apply fill-white;
        }
    }

    svg {
        transition: ease-in-out 0.2s;
    }

    span {
        @apply font-inter text-sm font-semibold leading-6 not-italic;
    }

    &:hover {
        background: linear-gradient(to right, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.01));
    }

    .icon {
        @apply text-gray-500;
    }
}
    .nav-item :deep(.p-badge-root) {
        @apply leading-5;
    }
</style>
<style lang="scss">
/* Body scroll lock for components that toggle this class directly
   (AddMemberTypeSelect); everything else uses useScrollLock() */
html.pb-scroll-lock, body.pb-scroll-lock {
  overflow: hidden !important;
  position: fixed;
  width: 100vw;
  overscroll-behavior: none;
}
</style>
